import { HttpErrorResponse } from '@angular/common/http';
import { DestroyRef, inject, Injectable, NgZone, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CalAngularService } from '@cvx/cal-angular';
import {
  catchError,
  defer,
  EMPTY,
  exhaustMap,
  filter,
  map,
  merge,
  Observable,
  of,
  repeat,
  retry,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
  throwError,
  timer,
} from 'rxjs';

import { SessionAuditApiService } from './session-audit-api.service';
import { SessionStorageService } from './session-storage.service';
import { UserActivityService } from './user-activity.service';
import {
  HEARTBEAT_INTERVAL_MS,
  HeartbeatState,
  IDLE_THRESHOLD_MS,
  LEASE_REFRESH_MS,
  LEASE_STALE_MS,
  RETRY_BASE_MS,
  RETRY_COUNT,
  SESSION_KEY,
  SESSION_STALE_MS,
  SessionInfo,
} from './session.models';

@Injectable({ providedIn: 'root' })
export class SessionAuditService {
  private readonly api = inject(SessionAuditApiService);
  private readonly store = inject(SessionStorageService);
  private readonly activity = inject(UserActivityService);
  private readonly cal = inject(CalAngularService);
  private readonly zone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);

  private readonly tabId = crypto.randomUUID();
  private readonly joinedAt = Date.now();
  private readonly stop$ = new Subject<void>();
  private readonly resume$ = new Subject<void>();
  private readonly state = signal<HeartbeatState>('stopped');

  private initialized = false;
  private unloadHandled = false;

  readonly heartbeatState = this.state.asReadonly();

  initialize(): void {
    if (this.initialized) {
      return;
    }
    this.initialized = true;

    this.registerBrowserListeners();

    // `isUserSignedIn()` reports the state at the moment it is called and
    // completes. On the load that returns from the MSAL redirect the account is
    // not in the cache yet, so a single check would miss the sign-in entirely.
    // `defer` makes every repeat call the service again instead of replaying the
    // first resolved promise.
    defer(() => this.cal.isUserSignedIn())
      .pipe(
        repeat({ delay: () => timer(LEASE_REFRESH_MS) }),
        filter((signedIn) => signedIn === true),
        take(1),
        takeUntil(this.stop$),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.start());
  }

  endSession(): Observable<void> {
    const session = this.store.readSession();
    this.shutdown();

    if (session === null || session.status !== 'active' || session.sessionId === null) {
      return of(void 0);
    }

    return this.api.endSession(session.sessionId).pipe(catchError(() => of(void 0)));
  }

  private start(): void {
    this.state.set('active');
    this.startLeaseLoop();
    this.startHeartbeatLoop();
    this.startResumeWatcher();
  }

  /**
   * Fast loop. Owns lease refresh, leadership, idle state and session creation,
   * so a newly promoted leader adopts or creates a session within seconds
   * rather than waiting a full heartbeat interval.
   */
  private startLeaseLoop(): void {
    timer(0, LEASE_REFRESH_MS)
      .pipe(
        tap(() => this.onLeaseTick()),
        filter(() => this.isLeader()),
        exhaustMap(() => this.ensureSession().pipe(catchError(() => EMPTY))),
        takeUntil(this.stop$),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private startHeartbeatLoop(): void {
    merge(timer(HEARTBEAT_INTERVAL_MS, HEARTBEAT_INTERVAL_MS), this.resume$)
      .pipe(
        filter(() => this.isLeader() && !this.isIdle()),
        map(() => this.store.readSession()),
        filter(
          (session): session is SessionInfo =>
            session !== null && session.status === 'active' && session.sessionId !== null
        ),
        // `exhaustMap`, not `switchMap`: a retry sequence lasts up to 14s and
        // must not be cancelled mid-backoff by the next tick.
        exhaustMap((session) => this.heartbeatWithRecovery(session.sessionId as string)),
        takeUntil(this.stop$),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private startResumeWatcher(): void {
    this.activity.activity$
      .pipe(
        filter(() => this.state() === 'idle-paused'),
        tap(() => this.state.set('active')),
        takeUntil(this.stop$),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.resume$.next());
  }

  private onLeaseTick(): void {
    this.refreshLease();
    if (this.state() !== 'recovering') {
      this.state.set(this.isIdle() ? 'idle-paused' : 'active');
    }
  }

  private ensureSession(): Observable<string> {
    const now = Date.now();
    const existing = this.store.readSession();

    if (existing !== null && existing.status === 'active' && existing.sessionId !== null) {
      const lastSeen = existing.lastHeartbeatAt ?? existing.startedAt;
      if (now - lastSeen < SESSION_STALE_MS) {
        return of(existing.sessionId);
      }

      // No tab renewed this session recently: the tab that owned it actually
      // closed rather than reloaded (see `handleUnload`). Close it out
      // explicitly before claiming a fresh one.
      return this.api
        .endSession(existing.sessionId)
        .pipe(catchError(() => of(void 0)), switchMap(() => this.startNewSession()));
    }

    // Another tab claimed creation moments ago. Let it finish; a claim older
    // than the staleness window is treated as abandoned and re-claimed below.
    if (
      existing !== null &&
      existing.status === 'creating' &&
      now - existing.startedAt < LEASE_STALE_MS
    ) {
      return EMPTY;
    }

    return this.startNewSession();
  }

  private startNewSession(): Observable<string> {
    this.store.writeSession({
      sessionId: null,
      status: 'creating',
      startedAt: Date.now(),
      lastHeartbeatAt: null,
    });

    return this.api.startSession().pipe(
      retry({
        count: RETRY_COUNT,
        delay: (_error, attempt) => timer(RETRY_BASE_MS * 2 ** (attempt - 1)),
      }),
      tap((response) => {
        this.store.writeSession({
          sessionId: response.sessionId,
          status: 'active',
          startedAt: Date.now(),
          lastHeartbeatAt: null,
        });
        this.state.set('active');
      }),
      map((response) => response.sessionId)
    );
  }

  private heartbeatWithRecovery(sessionId: string): Observable<unknown> {
    return this.sendHeartbeat(sessionId).pipe(
      catchError((error: unknown) => {
        if (!this.isSessionGone(error)) {
          return EMPTY;
        }

        // Do not clear the key: removing it fires a `storage` event that other
        // tabs read as a logout. Overwrite it with a fresh creation claim.
        this.state.set('recovering');
        return this.startNewSession().pipe(
          switchMap((newSessionId) => this.sendHeartbeat(newSessionId)),
          catchError(() => EMPTY)
        );
      })
    );
  }

  private sendHeartbeat(sessionId: string): Observable<void> {
    return this.api.heartbeat(sessionId).pipe(
      retry({
        count: RETRY_COUNT,
        delay: (error: unknown, attempt) =>
          this.isSessionGone(error)
            ? throwError(() => error)
            : timer(RETRY_BASE_MS * 2 ** (attempt - 1)),
      }),
      tap(() => {
        const session = this.store.readSession();
        if (session !== null) {
          this.store.writeSession({ ...session, lastHeartbeatAt: Date.now() });
        }
        this.state.set('active');
      })
    );
  }

  private isSessionGone(error: unknown): boolean {
    return error instanceof HttpErrorResponse && (error.status === 404 || error.status === 410);
  }

  private isLeader(): boolean {
    return this.store.resolveLeader(this.store.readRegistry(), this.tabId).isSelf;
  }

  private isIdle(): boolean {
    const latest = Math.max(
      this.activity.lastActivityAt(),
      this.store.latestActivityAt(this.store.readRegistry())
    );
    return Date.now() - latest >= IDLE_THRESHOLD_MS;
  }

  private refreshLease(now: number = Date.now()): void {
    this.store.refreshLease(
      {
        tabId: this.tabId,
        joinedAt: this.joinedAt,
        lastSeenAt: now,
        lastActivityAt: this.activity.lastActivityAt(),
      },
      now
    );
  }

  private registerBrowserListeners(): void {
    const onUnload = (): void => this.handleUnload();
    const onPageShow = (event: Event): void => {
      if ((event as PageTransitionEvent).persisted) {
        this.handleRestore();
      }
    };
    const onVisibility = (): void => {
      if (document.visibilityState === 'hidden') {
        this.refreshLease();
      }
    };
    const onStorage = (event: StorageEvent): void => {
      if (event.key === SESSION_KEY && event.newValue === null) {
        this.shutdown();
      }
    };

    this.zone.runOutsideAngular(() => {
      // `pagehide` is primary: it fires on iOS Safari and bfcache eviction where
      // `beforeunload` does not. The once-only guard keeps the pair to one call.
      window.addEventListener('pagehide', onUnload);
      window.addEventListener('beforeunload', onUnload);
      window.addEventListener('pageshow', onPageShow);
      window.addEventListener('storage', onStorage);
      document.addEventListener('visibilitychange', onVisibility);
    });

    this.destroyRef.onDestroy(() => {
      window.removeEventListener('pagehide', onUnload);
      window.removeEventListener('beforeunload', onUnload);
      window.removeEventListener('pageshow', onPageShow);
      window.removeEventListener('storage', onStorage);
      document.removeEventListener('visibilitychange', onVisibility);
    });
  }

  private handleUnload(): void {
    if (this.unloadHandled) {
      return;
    }
    this.unloadHandled = true;

    // Do not end or clear the session here: `beforeunload`/`pagehide` fire
    // identically for a real tab close and for a same-tab refresh, and there
    // is no reliable way to tell them apart at this point. Ending eagerly
    // caused every solo-tab refresh to log a spurious End+Start pair.
    // `ensureSession` resumes the session on the next load if it is recent,
    // or closes it out as stale otherwise (see `SESSION_STALE_MS`); the
    // backend's own heartbeat TTL is the final safety net for closes where
    // the tab never reopens.
    this.store.removeLease(this.tabId);
  }

  /** The page came back from bfcache, so its earlier `pagehide` did not stick. */
  private handleRestore(): void {
    this.unloadHandled = false;
    this.refreshLease();
  }

  private shutdown(): void {
    this.unloadHandled = true;
    this.state.set('stopped');
    this.stop$.next();
    this.store.clearSession();
    this.store.clearRegistry();
  }
}
